import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { demoApi, authApi } from "../lib/api";
import SocialPreviews from "../components/SocialPreviews";
import { ArrowRight, Eye, EyeOff, Sparkles, Zap, Shield, ChevronDown, ArrowLeft, KeyRound } from "lucide-react";

// Read from either sessionStorage first, then URL params. sessionStorage
// takes priority for the case: user in armería → clicks "Crear cuenta" →
// lands on /registro?ref=armeria-<toolId>. We want them in signup mode
// AND we want to preserve the pending generate for auto-resume.
const shouldStartInSignup = (location) => {
  try {
    const p = new URLSearchParams(location.search || "");
    const signupFlag = p.get("signup") === "1" || p.get("intent") === "signup" || p.get("mode") === "signup";
    const hasRef = !!p.get("ref");
    const isSignupPath = /^\/(registro|signup)\/?$/i.test(location.pathname || "");
    if (hasRef) sessionStorage.setItem("laforja_signup_ref", (p.get("ref") || "").slice(0, 120));
    return signupFlag || hasRef || isSignupPath;
  } catch { return false; }
};

export default function AuthPage() {
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(() => !shouldStartInSignup(location));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [demoArticle, setDemoArticle] = useState(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [resetCodeValue, setResetCodeValue] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    demoApi.article().then(r => setDemoArticle(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLogin && !acceptTerms) {
      toast.error("Debes aceptar los Términos y Condiciones para registrarte");
      return;
    }
    setLoading(true);
    try {
      if (isLogin) { await login(email, password); }
      else {
        const ref = sessionStorage.getItem("laforja_signup_ref");
        await register(name, email, password, ref || null);
        if (ref) sessionStorage.removeItem("laforja_signup_ref");
      }
      toast.success(isLogin ? "Bienvenido de vuelta" : "Cuenta creada");
      // Determine where to send the user after successful auth. Priority:
      //   1. `?next=/armeria/...` in the URL (survives any storage clearing)
      //   2. localStorage['armeria_return_to'] (persistent across tabs)
      // Fallback → /dashboard.
      const params = new URLSearchParams(location.search || "");
      let dest = "/dashboard";
      const nextParam = params.get("next");
      if (nextParam && nextParam.startsWith("/armeria/")) {
        dest = nextParam;
      } else {
        try {
          const stored = localStorage.getItem("armeria_return_to");
          if (stored && stored.startsWith("/armeria/")) {
            dest = stored;
            localStorage.removeItem("armeria_return_to");
          }
        } catch { /* silent */ }
      }
      navigate(dest);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error de autenticación");
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Introduce tu email"); return; }
    setLoading(true);
    try {
      const res = await authApi.forgotPassword(email);
      setCodeSent(true);
      if (res.data.code) setResetCodeValue(res.data.code);
      toast.success("Codigo de recuperacion generado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetCode.trim() || !newPassword.trim()) { toast.error("Completa todos los campos"); return; }
    setLoading(true);
    try {
      await authApi.resetPassword(email, resetCode, newPassword);
      toast.success("Contrasena actualizada. Ya puedes iniciar sesion.");
      setForgotMode(false); setCodeSent(false); setResetCode(""); setNewPassword(""); setResetCodeValue("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar contrasena");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen flex" style={{ background: "#0B1622" }}>
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center overflow-hidden" style={{ background: "#091420" }}>
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse at 30% 50%, rgba(0,180,216,0.08) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(240,160,80,0.05) 0%, transparent 50%)" }} />
        <div className="relative z-10 max-w-md px-12">
          <div className="flex items-center gap-3 mb-10">
            <img src="/logo-full.png" alt="La Forja" className="h-16 w-16 rounded-xl object-contain" />
            <span className="text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#E2E8F0" }}>La Forja</span>
          </div>
          <p className="text-sm mb-1 -mt-6" style={{ color: "#94A3B8", fontFamily: "'Outfit'" }}>de Contenidos</p>
          <h2 className="text-3xl leading-snug mb-6 font-bold" style={{ fontFamily: "'Crimson Pro', serif", color: "#E2E8F0" }}>
            Forja contenido que <span className="text-gradient">convierte</span>
          </h2>
          <p className="text-sm mb-10" style={{ color: "#94A3B8", fontFamily: "'Outfit', sans-serif", lineHeight: 1.7 }}>
            Plataforma de creación de contenidos con IA. Genera artículos optimizados para SEO, con FAQ, clasificación automática e imágenes destacadas.
          </p>
          <div className="space-y-4">
            {[
              { icon: Sparkles, text: "Artículos SEO con Claude Sonnet", color: "#00D4FF" },
              { icon: Shield, text: "FAQ para GEO automático", color: "#4ADE80" },
              { icon: Zap, text: "Publicación directa en WordPress", color: "#F0A050" },
            ].map((f) => (
              <div key={f.text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${f.color}15` }}>
                  <f.icon className="w-4 h-4" style={{ color: f.color }} />
                </div>
                <span className="text-sm" style={{ color: "#CBD5E1" }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <div className="flex items-center gap-3">
              <img src="/logo-full.png" alt="La Forja" className="h-12 w-12 rounded-xl object-contain" />
              <span className="text-2xl font-bold" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>La Forja</span>
            </div>
            <span className="text-sm" style={{ color: "#94A3B8" }}>de Contenidos</span>
          </div>

          <h1 className="text-3xl font-bold mb-2 tracking-tight" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>
            {forgotMode ? "Recuperar contraseña" : (isLogin ? "Bienvenido" : "Crear cuenta")}
          </h1>
          <p className="mb-8 text-sm" style={{ color: "#94A3B8" }}>
            {forgotMode ? "Introduce tu email para recibir un codigo" : (isLogin ? "Accede a tu forja de contenidos" : "Empieza a forjar contenido con IA")}
          </p>

          {forgotMode ? (
            <div className="space-y-4">
              {!codeSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <Label className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>Email</Label>
                    <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com" required className="h-11"
                      style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                  </div>
                  <Button data-testid="send-reset-code-btn" type="submit" disabled={loading}
                    className="w-full h-11 rounded-full font-medium text-sm"
                    style={{ background: "linear-gradient(135deg, #F59E0B, #FBBF24)", color: "#0B1622" }}>
                    {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (
                      <><KeyRound className="w-4 h-4 mr-2" />Enviar codigo</>
                    )}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {resetCodeValue && (
                    <div className="rounded-lg p-3 text-center" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)" }}>
                      <p className="text-xs mb-1" style={{ color: "#94A3B8" }}>Tu codigo de recuperacion:</p>
                      <p className="text-2xl font-bold tracking-widest" style={{ color: "#4ADE80", fontFamily: "'Space Grotesk'" }}>{resetCodeValue}</p>
                      <p className="text-xs mt-1" style={{ color: "#64748B" }}>Valido 30 minutos</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>Codigo de recuperacion</Label>
                    <Input data-testid="reset-code" value={resetCode} onChange={(e) => setResetCode(e.target.value)}
                      placeholder="123456" required className="h-11 text-center text-lg tracking-widest"
                      style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0", fontFamily: "'Space Grotesk'" }} />
                  </div>
                  <div>
                    <Label className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>Nueva contraseña</Label>
                    <Input data-testid="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimo 6 caracteres" required className="h-11"
                      style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                  </div>
                  <Button data-testid="confirm-reset-btn" type="submit" disabled={loading}
                    className="w-full h-11 rounded-full font-medium text-sm"
                    style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                    {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : "Cambiar contraseña"}
                  </Button>
                </form>
              )}
              <button onClick={() => { setForgotMode(false); setCodeSent(false); setResetCodeValue(""); }}
                className="flex items-center gap-1 text-sm mx-auto" style={{ color: "#00D4FF" }}>
                <ArrowLeft className="w-3.5 h-3.5" />Volver al login
              </button>
            </div>
          ) : (
          <>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>Nombre</Label>
                <Input data-testid="auth-name-input" id="name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre" required={!isLogin} className="h-11"
                  style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>Email</Label>
              <Input data-testid="auth-email-input" id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" required className="h-11"
                style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm mb-1.5 block" style={{ color: "#94A3B8" }}>Contraseña</Label>
              <div className="relative">
                <Input data-testid="auth-password-input" id="password" type={showPassword ? "text" : "password"}
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres"
                  required className="h-11 pr-10" style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                <button type="button" data-testid="toggle-password-btn" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {!isLogin && (
              <div className="flex items-start gap-2 text-xs">
                <input
                  data-testid="accept-terms-checkbox"
                  type="checkbox"
                  id="accept-terms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-0.5 cursor-pointer"
                  style={{ accentColor: "#00D4FF" }}
                />
                <label htmlFor="accept-terms" className="cursor-pointer" style={{ color: "#94A3B8", lineHeight: 1.5 }}>
                  He leído y acepto los{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#00D4FF" }}>
                    Términos y Condiciones
                  </a>
                  , incluida la limitación de responsabilidad sobre el contenido generado por IA.
                </label>
              </div>
            )}
            <Button data-testid="auth-submit-btn" type="submit" disabled={loading || (!isLogin && !acceptTerms)}
              className="w-full h-11 rounded-full font-medium text-sm transition-all duration-200"
              style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (
                <>{isLogin ? "Iniciar sesión" : "Crear cuenta"}<ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>

          {isLogin && (
            <button data-testid="forgot-password-link" onClick={() => setForgotMode(true)}
              className="block w-full text-center mt-3 text-sm" style={{ color: "#64748B" }}>
              ¿Olvidaste tu contraseña?
            </button>
          )}

          <p className="text-center mt-6 text-sm" style={{ color: "#94A3B8" }}>
            {isLogin ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}
            <button data-testid="auth-toggle-btn" onClick={() => setIsLogin(!isLogin)}
              className="ml-1 font-medium" style={{ color: "#00D4FF" }}>
              {isLogin ? "Regístrate" : "Inicia sesión"}
            </button>
          </p>
          </>
          )}

          {/* Demo toggle */}
          {demoArticle && (
            <button data-testid="show-demo-btn" onClick={() => setShowDemo(!showDemo)}
              className="w-full mt-6 flex items-center justify-center gap-2 py-2 rounded-lg text-xs transition-all duration-200"
              style={{ color: "#00D4FF", background: "rgba(0,212,255,0.05)", border: "1px solid rgba(0,212,255,0.15)" }}>
              <Sparkles className="w-3.5 h-3.5" />
              {showDemo ? "Ocultar demo" : "Ver ejemplo de artículo generado"}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showDemo ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>

        {/* Demo section */}
        {showDemo && demoArticle && (
          <div className="w-full max-w-4xl mt-8 space-y-6 animate-fade-in-up px-4">
            <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>
                {demoArticle.title}
              </h3>
              <p className="text-sm mb-4" style={{ color: "#94A3B8" }}>{demoArticle.meta_description}</p>
              <div className="flex gap-2 mb-4">
                {demoArticle.keywords?.slice(0, 4).map((kw, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded" style={{ background: "rgba(0,212,255,0.06)", color: "#64B5F6", fontFamily: "'IBM Plex Mono'" }}>{kw}</span>
                ))}
              </div>
              <div className="article-body text-sm" style={{ fontFamily: "'Crimson Pro'", maxHeight: "200px", overflow: "hidden", maskImage: "linear-gradient(to bottom, black 60%, transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, black 60%, transparent 100%)" }}
                dangerouslySetInnerHTML={{ __html: demoArticle.content_html }} />
            </div>
            <SocialPreviews socialPosts={demoArticle.social_posts} readOnly />
            <p className="text-center text-xs" style={{ color: "#94A3B8" }}>
              Regístrate gratis para generar tu primer artículo con 5 créditos de bienvenida
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
